import type { Map as GlobeMap } from 'maplibre-gl';
import Style from 'ol/style/Style.js';
import type { DataImage } from '../shared/data-image.js';
import type { DataLayer } from '../shared/data-layer.js';
import { visibleDataFeatures } from '../shared/data-layer-display.js';

type ScreenImage = Extract<DataImage, { kind: 'screen' }>;
type Pixel = [number, number];
interface CachedImage { ready: Promise<HTMLCanvasElement>; canvas?: HTMLCanvasElement }
const images = new Map<string, CachedImage>();

export function dataImageCanvas(image: DataImage): Promise<HTMLCanvasElement> {
  const key = `${image.width}:${image.height}:${image.color}:${image.src}`;
  const existing = images.get(key);
  if (existing) return existing.ready;
  const element = new Image();
  element.src = image.src;
  const entry: CachedImage = { ready: element.decode().then(() => {
    const canvas = document.createElement('canvas');
    canvas.width = image.width; canvas.height = image.height;
    const context = canvas.getContext('2d')!;
    context.drawImage(element, 0, 0, canvas.width, canvas.height);
    if (image.color.toLowerCase() !== '#ffffff') {
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
      const rgb = [1, 3, 5].map(offset => parseInt(image.color.slice(offset, offset + 2), 16) / 255);
      for (let offset = 0; offset < pixels.data.length; offset += 4) {
        for (let channel = 0; channel < 3; channel++) pixels.data[offset + channel] *= rgb[channel];
      }
      context.putImageData(pixels, 0, 0);
    }
    entry.canvas = canvas;
    return canvas;
  }) };
  images.set(key, entry);
  return entry.ready;
}

function loadedImage(image: DataImage): HTMLCanvasElement | undefined { return images.get(`${image.width}:${image.height}:${image.color}:${image.src}`)?.canvas; }

export async function prepareDataImages(layers: readonly DataLayer[]): Promise<void> {
  await Promise.all(layers.filter(layer => layer.visible).flatMap(layer => visibleDataFeatures(layer).flatMap(feature => feature.image?.visible ? [dataImageCanvas(feature.image)] : [])));
}

function triangle(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement, a: Pixel, b: Pixel, c: Pixel, second: boolean): void {
  context.save();
  context.beginPath(); context.moveTo(...a); context.lineTo(...b); context.lineTo(...c); context.closePath(); context.clip();
  if (second) context.transform((b[0] - c[0]) / canvas.width, (b[1] - c[1]) / canvas.width, (c[0] - a[0]) / canvas.height, (c[1] - a[1]) / canvas.height, a[0], a[1]);
  else context.transform((b[0] - a[0]) / canvas.width, (b[1] - a[1]) / canvas.width, (c[0] - b[0]) / canvas.height, (c[1] - b[1]) / canvas.height, a[0], a[1]);
  context.drawImage(canvas, 0, 0); context.restore();
}

export function groundImageStyle(image: Extract<DataImage, { kind: 'ground' }>, changed: () => void): Style {
  void dataImageCanvas(image).then(changed).catch(() => undefined);
  const composite = document.createElement('canvas');
  return new Style({ zIndex: -1, renderer: (coordinates, state) => {
    const canvas = loadedImage(image);
    if (!canvas || !image.visible) return;
    // SAFETY: Ground image features have one polygon ring in NW, NE, SE, SW order.
    const ring = (coordinates as Pixel[][])[0];
    const context = state.context;
    const left = Math.max(0, Math.floor(Math.min(...ring.map(point => point[0]))));
    const top = Math.max(0, Math.floor(Math.min(...ring.map(point => point[1]))));
    const right = Math.min(context.canvas.width, Math.ceil(Math.max(...ring.map(point => point[0]))));
    const bottom = Math.min(context.canvas.height, Math.ceil(Math.max(...ring.map(point => point[1]))));
    if (right <= left || bottom <= top) return;
    composite.width = right - left; composite.height = bottom - top;
    const paint = composite.getContext('2d')!;
    paint.translate(-left, -top);
    paint.globalCompositeOperation = 'lighter';
    triangle(paint, canvas, ring[0], ring[1], ring[2], false);
    triangle(paint, canvas, ring[0], ring[2], ring[3], true);
    context.save(); context.globalAlpha *= image.opacity;
    context.drawImage(composite, left, top); context.restore();
  }, hitDetectionRenderer: (coordinates, state) => {
    // SAFETY: The same validated ground polygon supplies the hit detection ring.
    const ring = (coordinates as Pixel[][])[0];
    const context = state.context;
    context.beginPath(); context.moveTo(...ring[0]); ring.slice(1).forEach(point => context.lineTo(...point)); context.closePath(); context.fill();
  } });
}

function position(value: number, units: string, extent: number, scale: number): number {
  return units === 'fraction' ? value * extent : units === 'insetPixels' ? extent - value * scale : value * scale;
}

export function screenImagePlacement(image: ScreenImage, width: number, height: number, scale = 1) {
  const dimension = (value: number, units: string, extent: number, native: number) => value < 0 ? native * scale : value === 0 ? 0 : position(value, units, extent, scale);
  let w = dimension(image.size.x, image.size.xunits, width, image.width);
  let h = dimension(image.size.y, image.size.yunits, height, image.height);
  if (!w && !h) { w = image.width * scale; h = image.height * scale; }
  else if (!w) w = h * image.width / image.height;
  else if (!h) h = w * image.height / image.width;
  const x = position(image.screenXY.x, image.screenXY.xunits, width, scale) - position(image.overlayXY.x, image.overlayXY.xunits, w, scale);
  const y = height - position(image.screenXY.y, image.screenXY.yunits, height, scale) - h + position(image.overlayXY.y, image.overlayXY.yunits, h, scale);
  return { x, y, width: w, height: h, rotationX: x + position(image.rotationXY.x, image.rotationXY.xunits, w, scale), rotationY: y + h - position(image.rotationXY.y, image.rotationXY.yunits, h, scale) };
}

export function drawScreenImages(context: CanvasRenderingContext2D, layers: readonly DataLayer[], width: number, height: number, scale = 1): void {
  const screens = layers.filter(layer => layer.visible).sort((a, b) => a.position - b.position).flatMap(layer => visibleDataFeatures(layer).flatMap(feature => feature.image?.kind === 'screen' && feature.image.visible ? [feature.image] : []).sort((a, b) => a.drawOrder - b.drawOrder));
  for (const image of screens) {
    const canvas = loadedImage(image);
    if (!canvas) continue;
    const box = screenImagePlacement(image, width, height, scale);
    context.save(); context.globalAlpha *= image.opacity;
    context.translate(box.rotationX, box.rotationY); context.rotate(-image.rotation * Math.PI / 180); context.translate(-box.rotationX, -box.rotationY);
    context.drawImage(canvas, box.x, box.y, box.width, box.height); context.restore();
  }
}

export class ScreenImageCanvas {
  private readonly canvas = document.createElement('canvas');
  constructor(private readonly container: HTMLElement, private readonly layers: () => readonly DataLayer[]) {
    this.canvas.className = 'data-screen-images';
    this.canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:6';
    container.appendChild(this.canvas);
  }
  draw = (): void => {
    const ratio = window.devicePixelRatio || 1;
    const width = Math.round(this.container.clientWidth * ratio), height = Math.round(this.container.clientHeight * ratio);
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    const context = this.canvas.getContext('2d')!;
    context.clearRect(0, 0, width, height);
    drawScreenImages(context, this.layers(), this.canvas.width, this.canvas.height, ratio);
  };
  refresh(): void { this.draw(); void prepareDataImages(this.layers()).then(this.draw).catch(() => undefined); }
  destroy(): void { this.canvas.remove(); }
}

interface GlobeImageLayers { layers: () => readonly DataLayer[]; ready: () => Promise<void> }
const globeImageLayers = new WeakMap<GlobeMap, GlobeImageLayers>();
export function registerGlobeImageLayers(map: GlobeMap, layers: () => readonly DataLayer[], ready: () => Promise<void>): void { globeImageLayers.set(map, { layers, ready }); }
export function getGlobeImageLayers(map: GlobeMap): readonly DataLayer[] { return globeImageLayers.get(map)?.layers() ?? []; }
export async function prepareGlobeDataImages(map: GlobeMap): Promise<void> {
  const entry = globeImageLayers.get(map);
  if (entry) await Promise.all([prepareDataImages(entry.layers()), entry.ready()]);
}
